import { useEffect, useRef, useState } from "react";
import type { ChangeEvent, CSSProperties } from "react";
import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { InputText } from "primereact/inputtext";
import { MultiSelect } from "primereact/multiselect";
import { OverlayPanel } from "primereact/overlaypanel";
import type { PaginatorTemplateOptions } from "primereact/paginator";
import { Toast } from "primereact/toast";

import { getErrorMessage, isAPIError } from "@/core/services";
import { useTableExport } from "@/modules/export";
import { usePermissionOptions } from "@/modules/permissions";
import {
  createRole,
  updateRole,
  useRoles,
  deleteRole,
  listAllRoles,
  DEFAULT_ROLE_SORT,
} from "@/modules/roles";
import type { Role } from "@/modules/roles";
import {
  ACTION_TOOLTIP_OPTIONS,
  DELETE_ACTION_TOOLTIP,
  EDIT_ACTION_TOOLTIP,
} from "../components/action-tooltip";
import { EmptyState, resolveEmptyStateKind } from "../components/empty-state";
import type { EmptyStateContent } from "../components/empty-state";
import { createExportFeedback, ExportTableDialog } from "../components/export-table-dialog";
import { StateMessage } from "../components/state-message";
import { RoleFormDialog } from "./components/role-form-dialog";
import type { RoleFormValues } from "./components/role-form-dialog";
import { useNavigate } from "react-router-dom";

import { Paths, buildUsersPath } from "@/core/constants/paths";
import { DeleteRoleDialog, RoleInUseDialog } from "./components/delete-role";
import { NO_PERMISSIONS_LABEL } from "./roles.constants";
import styles from "./roles-screen.module.scss";

/** Base sem perfis: a listagem está vazia porque ainda não há o que exibir. */
const EMPTY_STATE: EmptyStateContent = {
  title: "Nenhum perfil cadastrado",
  description: "Assim que houver perfis no Hub, eles aparecerão nesta listagem.",
};

/** Busca no input sem registros — `ilu-no-search` do Figma. */
const EMPTY_SEARCH_STATE: EmptyStateContent = {
  title: "Nenhum perfil encontrado para essa busca.",
  description: "Verifique o termo digitado ou tente buscar com outras palavras.",
};

/** Filtros aplicados sem registros — `ilu-no-filters` do Figma. */
const EMPTY_FILTER_STATE: EmptyStateContent = {
  title: "Nenhum perfil corresponde aos filtros selecionados.",
  description: "Tente ajustar ou limpar os filtros",
};

/** MS002 — confirmação da exclusão concluída. */
export const DELETE_SUCCESS_MESSAGE = "Perfil excluído com sucesso";
export const DELETE_ERROR_MESSAGE = "Não foi possível excluir o perfil.";
export const DELETE_FORBIDDEN_MESSAGE = "Você não tem permissão para excluir os perfis do Hub.";

/** O serviço recusa a exclusão de um perfil ainda vinculado a usuários (RN03). */
const CONFLICT_STATUS = 409;

export const CREATE_SUCESS_SUMMARY = "Perfil criado com sucesso";
export const CREATE_SUCCESS_MESSAGE =
  "Para adicionar usuários no perfil, vá para a seção usuários.";
export const UPDATE_SUCESS_SUMMARY = "Perfil atualizado com novas permissões";
export const UPDATE_SUCCESS_MESSAGE = "As alterações foram salvas com sucesso.";
export const CREATE_ERROR_MESSAGE = "Não foi possível cadastrar o perfil. Tente novamente.";
export const UPDATE_ERROR_MESSAGE = "Não foi possível atualizar o perfil. Tente novamente.";

const DEFAULT_ROWS_PER_PAGE = 10;
const PERMISSION_VIRTUAL_SCROLL_THRESHOLD = 50;
const PERMISSION_ITEM_SIZE = 40;

const PERMISSION_FILTER_PLACEHOLDER = "Buscar permissão";
const PERMISSION_EMPTY_FILTER_MESSAGE = "Nenhuma permissão encontrada.";

const ROWS_PER_PAGE_OPTIONS = [10, 25, 50];

const ROWS_PER_PAGE_DROPDOWN_OPTIONS = ROWS_PER_PAGE_OPTIONS.map((rows) => ({
  label: `${rows} por página`,
  value: rows,
}));

const CURRENT_PAGE_REPORT = "Mostrando {first} a {last} de {totalRecords} registros";

const PAGINATOR_TEMPLATE: PaginatorTemplateOptions = {
  layout:
    "CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown",
  RowsPerPageDropdown: (options) => (
    <Dropdown
      className="p-paginator-rpp-options"
      panelClassName={styles.rowsPerPagePanel}
      value={options.value}
      options={ROWS_PER_PAGE_DROPDOWN_OPTIONS}
      onChange={options.onChange}
      aria-label="Registros por página"
    />
  ),
};

/** Colunas visíveis da listagem, na ordem da tabela (AC2 da US1442). */
const EXPORT_COLUMNS = ["Código", "Nome", "Permissões", "Usuários"];

const EXPORT_FILE_NAME = "perfis";
const EXPORT_TITLE = "Perfis";

function toExportRow(role: Role): string[] {
  const permissions =
    role.permissoes.length === 0
      ? NO_PERMISSIONS_LABEL
      : role.permissoes.map((permission) => permission.nome || permission.codigo).join(", ");

  return [role.codigo, role.nome, permissions, String(role.quantidadeUsuarios)];
}

const COLUMN_WIDTHS: Record<string, CSSProperties> = {
  codigo: { minWidth: "10.9375rem" },
  nome: { minWidth: "12rem" },
  permissoes: { minWidth: "18rem" },
  quantidadeUsuarios: { width: "8rem" },
  acoes: { width: "8.5rem" },
};

/** `null` mantém o pop-up fechado; na inclusão não há perfil a editar. */
type RoleFormState = { role: Role | null } | null;

export function RolesScreen() {
  const navigate = useNavigate();
  const configPanel = useRef<OverlayPanel>(null);
  const toast = useRef<Toast>(null);

  const [search, setSearch] = useState("");
  const [permissaoIds, setPermissaoIds] = useState<string[]>([]);
  const [first, setFirst] = useState(0);
  const [rows, setRows] = useState(DEFAULT_ROWS_PER_PAGE);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [roleForm, setRoleForm] = useState<RoleFormState>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);
  const [blocked, setBlocked] = useState<Role | null>(null);

  const permissionFilter = usePermissionOptions();
  const permissionVirtualScroller =
    permissionFilter.options.length > PERMISSION_VIRTUAL_SCROLL_THRESHOLD
      ? { itemSize: PERMISSION_ITEM_SIZE }
      : undefined;

  const { roles, total, status, errorMessage, reload } = useRoles({
    search,
    permissaoIds,
    page: Math.floor(first / rows) + 1,
    pageSize: rows,
    sortField: DEFAULT_ROLE_SORT.field,
    sortOrder: DEFAULT_ROLE_SORT.order,
  });

  /** AC2 — o arquivo leva o resultado completo da consulta, e não a página em tela. */
  const exportView = useTableExport({
    loadView: async () => {
      const allRoles = await listAllRoles({
        pesquisa: search,
        permissaoIds,
        ordenarPor: DEFAULT_ROLE_SORT.field,
        ordem: DEFAULT_ROLE_SORT.order,
      });

      return {
        fileName: EXPORT_FILE_NAME,
        title: EXPORT_TITLE,
        columns: EXPORT_COLUMNS,
        rows: allRoles.map(toExportRow),
      };
    },
    ...createExportFeedback(toast),
  });

  const isLoading = status === "loading";
  const hasSearch = search.trim() !== "";
  const hasFilters = permissaoIds.length > 0;
  const emptyKind = resolveEmptyStateKind(hasSearch, hasFilters);
  const emptyContent =
    emptyKind === "search"
      ? EMPTY_SEARCH_STATE
      : emptyKind === "filter"
        ? EMPTY_FILTER_STATE
        : EMPTY_STATE;

  // Só depois da consulta concluída: enquanto ela corre, a tabela segue em tela
  // com o indicador de carregamento e os registros da consulta anterior.
  const isEmpty = status === "success" && roles.length === 0;

  useEffect(() => {
    setFirst((current) => (current < total ? current : 0));
  }, [total]);

  function handleSearch(term: string) {
    setSearch(term);
    setFirst(0);
  }

  function handlePermissionFilter(selected: string[]) {
    setPermissaoIds(selected);
    setFirst(0);
  }

  function handleClearSearch() {
    handleSearch("");
  }

  function handleClearFilters() {
    handlePermissionFilter([]);
  }

  function handlePage(event: { first: number; rows: number }) {
    setFirst(event.first);
    setRows(event.rows);
  }

  /** RN001 — o menu ⋮ abre o modal Exportar Tabela. */
  function handleExportView() {
    configPanel.current?.hide();
    exportView.open();
  }

  function handleNewRole() {
    setRoleForm({ role: null });
  }

  function handleEdit(role: Role) {
    setRoleForm({ role });
  }

  function handleGoToUsers(role: Role) {
    setBlocked(null);
    navigate(buildUsersPath({ papelId: role.id }));
  }

  // RN03: o perfil com usuários vinculados nem chega ao pop-up de confirmação.
  function handleDelete(role: Role) {
    if (role.quantidadeUsuarios > 0) {
      setBlocked(role);
      return;
    }

    setDeleting(role);
  }

  function showDeleteError(error: unknown) {
    const isForbidden = isAPIError(error) && error.status === 403;

    toast.current?.show({
      severity: "error",
      summary: isForbidden ? "Acesso não autorizado" : DELETE_ERROR_MESSAGE,
      detail: isForbidden ? DELETE_FORBIDDEN_MESSAGE : getErrorMessage(error),
      life: 5000,
      closable: true,
    });
  }

  /** Nunca rejeita: a falha mantém o pop-up aberto e o perfil na listagem. */
  async function handleConfirmDelete(role: Role) {
    try {
      await deleteRole({ id: role.id });
      setDeleting(null);

      toast.current?.show({
        severity: "success",
        summary: "Sucesso",
        detail: DELETE_SUCCESS_MESSAGE,
        life: 3000,
        closable: true,
      });

      // Recarrega a página atual preservando pesquisa, filtro e paginação.
      reload();
    } catch (error) {
      // A contagem da listagem pode estar defasada: quem decide é o serviço.
      if (isAPIError(error) && error.status === CONFLICT_STATUS) {
        setDeleting(null);
        setBlocked(role);
        return;
      }

      showDeleteError(error);
    }
  }

  function showSaveError(error: unknown, isEditing: boolean) {
    const isForbidden = isAPIError(error) && error.status === 403;

    toast.current?.show({
      severity: "error",
      summary: isForbidden
        ? "Acesso não autorizado"
        : isEditing
          ? UPDATE_ERROR_MESSAGE
          : CREATE_ERROR_MESSAGE,
      life: 5000,
      closable: true,
    });
  }

  /** Nunca rejeita: a falha mantém o pop-up aberto com os dados preenchidos. */
  async function handleSaveRole(role: Role | null, values: RoleFormValues) {
    try {
      if (role) {
        await updateRole({ id: role.id, ...values });
      } else {
        await createRole(values);
      }

      setRoleForm(null);
      reload();

      toast.current?.show({
        severity: "success",
        summary: role ? UPDATE_SUCESS_SUMMARY : CREATE_SUCESS_SUMMARY,
        detail: role ? UPDATE_SUCCESS_MESSAGE : CREATE_SUCCESS_MESSAGE,
        life: 3000,
        closable: true,
      });
    } catch (error) {
      showSaveError(error, Boolean(role));
    }
  }

  function renderPermissions(role: Role) {
    return (
      <ul className={styles.tagList}>
        {role.permissoes.length === 0 ? (
          <li className={styles.tag}>{NO_PERMISSIONS_LABEL}</li>
        ) : (
          role.permissoes.map((permission) => (
            <li key={permission.id} className={styles.tag}>
              {permission.nome || permission.codigo}
            </li>
          ))
        )}
      </ul>
    );
  }

  function renderUsers(role: Role) {
    const count = role.quantidadeUsuarios;
    return <span className={styles.usersCount}>{count}</span>;
  }

  function renderActions(role: Role) {
    return (
      <div className={styles.rowActions}>
        <Button
          type="button"
          className={styles.rowActionButton}
          icon="pi pi-pencil"
          aria-label={`Editar perfil ${role.nome}`}
          tooltip={EDIT_ACTION_TOOLTIP}
          tooltipOptions={ACTION_TOOLTIP_OPTIONS}
          onClick={() => handleEdit(role)}
          data-testid={`roles-edit-${role.id}`}
          text
        />
        <Button
          type="button"
          className={styles.rowActionButton}
          icon="pi pi-trash"
          aria-label={`Excluir perfil ${role.nome}`}
          tooltip={DELETE_ACTION_TOOLTIP}
          tooltipOptions={ACTION_TOOLTIP_OPTIONS}
          onClick={() => handleDelete(role)}
          data-testid={`roles-delete-${role.id}`}
          text
        />
      </div>
    );
  }

  if (status === "forbidden") {
    return (
      <section className={styles.container} data-testid="roles-screen">
        <StateMessage
          icon="pi pi-lock"
          title="Acesso não autorizado"
          description={errorMessage ?? undefined}
          testId="roles-forbidden"
        />
      </section>
    );
  }

  return (
    <section className={styles.container} data-testid="roles-screen">
      <Toast ref={toast} position="top-right" />

      <div className={styles.toolbar}>
        <div className={styles.filters}>
          <div className={styles.searchField}>
            <label className={styles.srOnly} htmlFor="roles-search">
              Pesquisar perfis
            </label>
            <InputText
              id="roles-search"
              className={styles.searchInput}
              type="search"
              value={search}
              placeholder="Buscar por código, nome..."
              onChange={(event: ChangeEvent<HTMLInputElement>) =>
                handleSearch(event.currentTarget.value)
              }
              data-testid="roles-search"
            />
            <i className={`pi pi-search ${styles.searchIcon}`} aria-hidden />
          </div>

          <MultiSelect
            className={styles.permissionFilter}
            panelClassName={styles.permissionFilterPanel}
            value={permissaoIds}
            options={permissionFilter.options}
            onChange={(event) => handlePermissionFilter((event.value as string[]) ?? [])}
            onShow={() => setIsFilterOpen(true)}
            onHide={() => setIsFilterOpen(false)}
            dropdownIcon={isFilterOpen ? "pi pi-chevron-up" : "pi pi-chevron-down"}
            optionLabel="label"
            optionValue="value"
            placeholder="Permissões"
            filter
            filterPlaceholder={PERMISSION_FILTER_PLACEHOLDER}
            virtualScrollerOptions={permissionVirtualScroller}
            showSelectAll={false}
            showClear
            maxSelectedLabels={1}
            selectedItemsLabel="{0} permissões"
            emptyMessage={
              permissionFilter.hasError
                ? "Não foi possível carregar as permissões."
                : "Nenhuma permissão cadastrada."
            }
            emptyFilterMessage={PERMISSION_EMPTY_FILTER_MESSAGE}
            disabled={permissionFilter.isLoading}
            aria-label="Filtrar por permissões"
            data-testid="roles-permission-filter"
          />
        </div>

        <div className={styles.toolbarActions}>
          <Button
            type="button"
            className={styles.newRoleButton}
            icon="pi pi-plus"
            label="Novo Perfil"
            onClick={handleNewRole}
            data-testid="roles-new"
          />

          <Button
            type="button"
            className={styles.configButton}
            icon="pi pi-ellipsis-v"
            aria-label="Opções da tabela"
            aria-haspopup
            onClick={(event) => configPanel.current?.toggle(event)}
            data-testid="roles-table-config"
            severity="secondary"
            outlined
          />

          <OverlayPanel ref={configPanel} className={styles.configPanel} dismissable>
            <div className={styles.configBody} data-testid="roles-table-config-panel">
              <button
                type="button"
                className={styles.configAction}
                onClick={handleExportView}
                data-testid="roles-export-view"
              >
                <i className="pi pi-download" aria-hidden />
                Exportar Visualização
              </button>
            </div>
          </OverlayPanel>
        </div>
      </div>

      {status === "error" ? (
        <StateMessage
          icon="pi pi-exclamation-triangle"
          title="Não foi possível carregar os perfis."
          description={errorMessage ?? undefined}
          testId="roles-error"
        >
          <Button
            type="button"
            className={styles.retryButton}
            label="Tentar novamente"
            icon="pi pi-refresh"
            onClick={reload}
            data-testid="roles-retry"
          />
        </StateMessage>
      ) : (
        <div className={styles.tableWrapper}>
          {isLoading ? (
            <span className={styles.srOnly} role="status" data-testid="roles-loading">
              Carregando perfis...
            </span>
          ) : null}

          {isEmpty ? (
            <EmptyState
              kind={emptyKind}
              content={emptyContent}
              onClear={
                emptyKind === "search"
                  ? handleClearSearch
                  : emptyKind === "filter"
                    ? handleClearFilters
                    : undefined
              }
              testId="roles-empty"
            />
          ) : (
            <DataTable
              className={styles.table}
              value={roles}
              dataKey="id"
              loading={isLoading}
              tableStyle={{ minWidth: "58rem" }}
              emptyMessage={emptyContent.title}
              lazy
              totalRecords={total}
              paginator={total > 0}
              paginatorClassName={styles.paginator}
              paginatorTemplate={PAGINATOR_TEMPLATE}
              currentPageReportTemplate={CURRENT_PAGE_REPORT}
              rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
              first={first}
              rows={rows}
              onPage={handlePage}
              aria-label="Perfis cadastrados"
              data-testid="roles-table"
            >
              <Column field="codigo" header="Código" style={COLUMN_WIDTHS.codigo} />
              <Column field="nome" header="Nome" style={COLUMN_WIDTHS.nome} />
              <Column
                header="Permissões"
                style={COLUMN_WIDTHS.permissoes}
                body={renderPermissions}
              />
              <Column
                field="quantidadeUsuarios"
                header="Usuários"
                style={COLUMN_WIDTHS.quantidadeUsuarios}
                body={renderUsers}
              />
              <Column
                header="Ações"
                headerClassName={styles.actionsHeader}
                bodyClassName={styles.actionsCell}
                style={COLUMN_WIDTHS.acoes}
                body={renderActions}
              />
            </DataTable>
          )}
        </div>
      )}

      {exportView.isOpen ? (
        <ExportTableDialog
          listName="perfis"
          isExporting={exportView.isExporting}
          onExport={exportView.run}
          onCancel={exportView.close}
        />
      ) : null}

      {roleForm ? (
        // A chave descarta o formulário ao trocar de perfil, sem efeito de sincronização.
        <RoleFormDialog
          key={roleForm.role?.id ?? "novo"}
          role={roleForm.role}
          permissions={permissionFilter.permissions}
          isLoadingPermissions={permissionFilter.isLoading}
          hasPermissionsError={permissionFilter.hasError}
          onSubmit={(values) => handleSaveRole(roleForm.role, values)}
          onCancel={() => setRoleForm(null)}
        />
      ) : null}

      {deleting ? (
        // A chave descarta o estado de envio ao trocar de perfil selecionado.
        <DeleteRoleDialog
          key={deleting.id}
          role={deleting}
          onConfirm={() => handleConfirmDelete(deleting)}
          onCancel={() => setDeleting(null)}
        />
      ) : null}

      {blocked ? (
        <RoleInUseDialog
          role={blocked}
          onClose={() => setBlocked(null)}
          onGoToUsers={() => handleGoToUsers(blocked)}
        />
      ) : null}
    </section>
  );
}
